import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { setUser } from "../../redux/auth/actions";
import api from "../../services/api";
import Loader from "../../components/loader";
import selegoLogo from "../../assets/selego.png";

export default function OAuthAuthorize() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.Auth.user);

  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const params = {
    client_id: searchParams.get("client_id"),
    redirect_uri: searchParams.get("redirect_uri"),
    response_type: searchParams.get("response_type"),
    code_challenge: searchParams.get("code_challenge"),
    code_challenge_method: searchParams.get("code_challenge_method"),
    state: searchParams.get("state"),
    scope: searchParams.get("scope"),
    resource: searchParams.get("resource"),
  };

  const missing = !params.client_id || !params.redirect_uri || !params.code_challenge;

  useEffect(() => {
    (async () => {
      if (missing) {
        setLoading(false);
        return;
      }

      let current = user;
      if (!current) {
        try {
          const r = await api.get("/user/signin_token");
          if (r.ok && r.user) {
            api.setToken(r.token);
            dispatch(setUser(r.user));
            current = r.user;
          }
        } catch {
          // not logged in
        }
      }

      if (!current) {
        const redirect = encodeURIComponent(location.pathname + location.search);
        navigate(`/auth?redirect=${redirect}`);
        return;
      }

      try {
        const r = await api.get(`/oauth/authorize/client?client_id=${encodeURIComponent(params.client_id)}`);
        if (!r.ok) throw new Error(r.code);
        setClient(r.data);
      } catch (e) {
        toast.error("Invalid OAuth request");
      }
      setLoading(false);
    })();
  }, []);

  async function handleGrant() {
    setSubmitting(true);
    try {
      const r = await api.post("/oauth/authorize/grant", params);
      if (!r.ok) throw new Error(r.code);
      window.location.href = r.data.redirect_url;
    } catch (e) {
      toast.error("Unable to authorize: " + (e.code || e.message || "error"));
      setSubmitting(false);
    }
  }

  async function handleDeny() {
    setSubmitting(true);
    try {
      const r = await api.post("/oauth/authorize/deny", { redirect_uri: params.redirect_uri, state: params.state });
      if (!r.ok) throw new Error(r.code);
      window.location.href = r.data.redirect_url;
    } catch (e) {
      toast.error("Error: " + (e.code || e.message));
      setSubmitting(false);
    }
  }

  if (loading) return <Loader />;

  if (missing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg border border-gray-200 p-6 text-center">
          <h1 className="text-lg font-semibold text-gray-900">Invalid request</h1>
          <p className="text-sm text-gray-500 mt-2">This OAuth authorization link is missing required parameters.</p>
        </div>
      </div>
    );
  }

  const scopes = (params.scope || "").split(" ").filter(Boolean);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg border border-gray-200 shadow-sm p-8">
        <div className="flex items-center justify-center gap-6 mb-6">
          {client?.logo_uri ? (
            <img src={client.logo_uri} alt={client.client_name} className="w-14 h-14 rounded-lg object-contain" />
          ) : (
            <div className="w-14 h-14 rounded-lg bg-gray-200 flex items-center justify-center text-xl font-semibold text-gray-500">
              {(client?.client_name || "?").charAt(0).toUpperCase()}
            </div>
          )}
          <div className="text-gray-400 text-xl">⇄</div>
          <img src={selegoLogo} alt="Selego" className="w-14 h-14 rounded-lg object-contain" />
        </div>

        <h1 className="text-xl font-semibold text-gray-900 text-center">
          Authorize <span className="text-blue-600">{client?.client_name || params.client_id}</span>
        </h1>
        <p className="text-sm text-gray-500 text-center mt-2">
          This application will be able to access your accounting account on your behalf.
        </p>

        <div className="mt-6 bg-gray-50 rounded-md p-4 border border-gray-200">
          <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Signed in as</div>
          <div className="text-sm text-gray-900">{user?.name}</div>
          <div className="text-xs text-gray-500">{user?.email}</div>
        </div>

        {scopes.length > 0 && (
          <div className="mt-4">
            <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Permissions</div>
            <ul className="text-sm text-gray-700 space-y-1">
              {scopes.map((s) => (
                <li key={s} className="flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{s}</code>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-6 flex gap-3">
          <button
            onClick={handleDeny}
            disabled={submitting}
            className="flex-1 px-4 py-2 text-sm font-medium border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
          <button
            onClick={handleGrant}
            disabled={submitting}
            className="flex-1 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
            {submitting ? "Authorizing…" : "Authorize"}
          </button>
        </div>

        <p className="text-xs text-gray-400 text-center mt-4">
          You can revoke access at any time from <span className="underline">Connected apps</span> in your account settings.
        </p>
      </div>
    </div>
  );
}
